/**
 * Buy / deploy screen (VISUAL-SPEC "Buy/deploy screen"), mounted as the game's
 * bottom bar over the live board. Left: unit chips with prices and −/+ counts.
 * Middle: your fleet roster — every purchased ship listed individually. Right:
 * enemy-admiral level, running budget, and the Suggest / Deploy actions.
 *
 * Each ship in the Fleet window has two port pickers: "From" (which owned/home
 * city it spawns at) and "To" (its destination course). A dotted trail is drawn
 * on the board from each ship's home port to its destination. Courses are a
 * planning aid — the rules never move a ship on its own ("follow the dots").
 *
 * A programmatic handle (fillSuggested / add / deploy / budgetLeft) is exposed for
 * the play E2E and the buy-screen shot.
 */
import { suggestFleet } from '../rules/ai';
import { addShip, UNITS } from '../rules/ships';
import { separateShips } from '../rules/deploy';
import { ICON_FLAG, ICON_X, ICON_ARROW, ICON_VP, ICON_ZAP, ICON_PLAY } from './icons';
import type { BoardView } from '../render/geometry';
import type { City, GameState, ShipType, Side } from '../rules/state';

const ORDER: ShipType[] = ['CV', 'BB', 'CR', 'SS', 'DD', 'PT', 'AT', 'TR'];

const NOTE: Record<ShipType, string> = {
  CV: 'Spots 2 tiles; +1 air dmg', BB: 'Heaviest guns', CR: 'Sub hunter',
  SS: 'Hidden when submerged', DD: 'Fast sub hunter', PT: 'Cheap scout',
  AT: 'Claims & fights light', TR: 'Claims cities',
  FT: 'Immobile coastal gun (built, not bought)', // not in the buy list (ORDER)
};

// Stepper glyphs as FILLED SVG shapes (minus bar / plus cross) so they can render
// white with a black border (styled in CSS). Shared shape for the zoom buttons too.
const ICON_MINUS = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="6.7" width="10" height="2.6" rx="1.3"/></svg>';
const ICON_PLUS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.7 3 H9.3 V6.7 H13 V9.3 H9.3 V13 H6.7 V9.3 H3 V6.7 H6.7 Z"/></svg>';

const CSS = `
/* The buy MENU is mounted as the game's real bottom bar (.ar-buy-bar), so it never
   covers the canvas and the whole map stays draggable/pannable. Colours come from
   the .ar-game design tokens (--brass / --wood / --parch …) so the buy chrome
   matches the game chrome exactly. Course-setting lives in a separate floating
   "Fleet orders" window on the left (.ar-owin, styled in game.ts). */
.ar-buy-bar { flex: 0 0 auto; display: flex; min-height: 136px;
  font-family: Georgia, 'Times New Roman', serif; color: var(--txt); line-height: 1.4;
  background: linear-gradient(180deg, hsl(45, 57.1%, 86.3%), var(--parch) 30%, var(--parch2));
  border: 2px solid var(--wood); border-top: 3px solid var(--brass-d); }

/* Width structure mirrors the play bar: the big flex-grow section (ship chips,
   like the move log) leads on the left, then a modest middle column (the
   briefing, like .ar-sel) and the right controls column — each divided by a wood
   rule. Layout is driven purely by these flex widths (no media queries): the
   columns share space and shrink by width alone. */
/* The chips strip must never dictate the bar height: it fills whatever height the
   sibling columns (briefing · budget/buttons) need and SCROLLS within it. The
   scrolling grid is taken OUT OF FLOW (absolute), so the outer .ar-buy-chips has
   no intrinsic height and just stretches to the flex line set by the siblings. */
.ar-buy-chips { flex: 999 1 240px; min-width: 200px; position: relative; overflow: hidden;
  border-right: 2px solid var(--wood); box-shadow: inset -1px 0 0 hsla(0, 0%, 100%, 0.35); }
/* Uniform grid: every column the same width; cards wrap into rows and the strip
   scrolls vertically when the rows exceed the bar height. Same brass scrollbar as
   the play bar's move log. */
.ar-buy-chips-grid { position: absolute; inset: 0; display: grid; gap: 8px; align-content: start;
  grid-template-columns: repeat(auto-fill, minmax(156px, 1fr));
  padding: 9px 13px; overflow: hidden auto;
  scrollbar-width: thin; scrollbar-color: var(--brass-d) hsla(0, 0%, 0%, 0.18); }
.ar-buy-chips-grid::-webkit-scrollbar { width: 11px; height: 9px; }
.ar-buy-chips-grid::-webkit-scrollbar-track { background: hsla(0, 0%, 0%, 0.18); border-radius: 6px; }
.ar-buy-chips-grid::-webkit-scrollbar-thumb { background: var(--brass-d); border-radius: 6px;
  border: 2px solid transparent; background-clip: content-box; }
.ar-buy-chips-grid::-webkit-scrollbar-thumb:hover { background: var(--brass); }
/* one ship card: name top-left + price tab top-right, description in the middle,
   a divider, then −/count/+ pinned along the bottom. min-height makes every card
   identical even when a description is a single line. */
/* NB: no overflow:hidden — that lets the grid size a row to the card's min-height
   and clip taller content (e.g. Armed Transport's wrapped 2-line title). The grid
   sizes rows to content instead; the price tab rounds its own corner. */
.ar-chip { display: flex; flex-direction: column; min-height: 110px;
  background: linear-gradient(180deg, hsl(45, 60%, 92.2%), hsl(43, 45.6%, 79.8%));
  border: 2px solid var(--wood); border-radius: 6px; box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.5); }
.ar-chip.sel { border-color: hsl(124, 44.4%, 33.1%); box-shadow: 0 0 0 2px hsl(105, 40.6%, 58.4%) inset; }
.ar-chip .hd { display: flex; justify-content: space-between; align-items: flex-start; }
.ar-chip .nm { font-weight: 700; font-size: 13px; color: var(--head); letter-spacing: 0.2px; padding: 5px 8px; }
.ar-chip .pr { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 2px;
  font-weight: 700; font-size: 12px; color: hsl(132, 48%, 32%); font-variant-numeric: tabular-nums;
  padding: 4px 7px; background: #ffffff;
  border-left: 1px solid var(--wood); border-bottom: 1px solid var(--wood);
  border-bottom-left-radius: 6px; border-top-right-radius: 4px; }
.ar-chip .pr svg { width: 1.15em; height: 1.15em; display: block; }
.ar-chip .desc { flex: 1; padding: 1px 8px 5px; font-size: 11px; color: var(--wood-d); line-height: 1.3; }
.ar-chip .ctl { display: flex; align-items: center; gap: 5px; padding: 5px 8px;
  border-top: 1px solid var(--wood); background: hsla(31, 47.6%, 28.4%, 0.1); }
.ar-chip .n { flex: 1; text-align: center; font-weight: 700; font-size: 14px; color: var(--head); font-variant-numeric: tabular-nums; }
.ar-chip button { display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; flex: 0 0 auto; cursor: pointer;
  border: 1px solid hsl(208, 55%, 30%); background: linear-gradient(180deg, hsl(205, 66%, 58%), hsl(208, 70%, 47%));
  border-radius: 4px; box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.25); }
.ar-chip button svg { pointer-events: none; width: 15px; height: 15px; display: block; }
.ar-chip button svg path, .ar-chip button svg rect { fill: #fff; stroke: #000; stroke-width: 1; stroke-linejoin: round; }
.ar-chip button:not(:disabled):hover { filter: brightness(1.06); }
.ar-chip button:not(:disabled):active { box-shadow: inset 0 2px 3px hsla(0, 0%, 0%, 0.3); }
/* disabled stepper (can't afford a +, or nothing to − ) — greyed and inert */
.ar-chip button:disabled { cursor: default; opacity: 0.4; box-shadow: none; }

/* right wrapper: transparent on wide screens (display:contents) so the
   budget/buttons column sits directly in the bar; below a width it becomes a real
   column (mirrors the game bar's .ar-right). */
.ar-buy-side { display: contents; }

/* right column — budget/ship info above the action buttons, fixed-width so the
   bar doesn't reflow as the numbers change (mirrors the play-phase .ar-right). */
.ar-buy-right { flex: 1 1 300px; max-width: 360px; display: flex; flex-direction: column; justify-content: center;
  gap: 9px; padding: 9px 13px; }
.ar-buy-budget { display: flex; justify-content: space-between; align-items: baseline; gap: 8px;
  font-weight: 700; font-size: 14px; color: var(--txt); }
/* colour the whole readout (currency star + number) green so the ✦ matches the digits */
.ar-buy-budget .bud { display: inline-flex; align-items: center; gap: 2px; color: hsl(132, 48%, 32%); }
.ar-buy-budget .bud b { font-variant-numeric: tabular-nums; }
.ar-buy-budget .bud svg { width: 1.15em; height: 1.15em; display: block; }
.ar-buy-budget .ships { color: var(--head); font-variant-numeric: tabular-nums; }
.ar-buy-msg { min-height: 1.25em; font-size: 12px; font-style: italic; color: var(--head); }
.ar-buy-msg:empty::before { content: 'Set home port & destination per ship in the Fleet window.'; color: hsl(31, 47.6%, 28.4%); }
/* two-up when there's room, else one button per row (auto-fit collapses the
   second track when it no longer fits) — mirrors the play bar's controls grid. */
.ar-buy-actions { display: grid; gap: 7px; grid-template-columns: repeat(auto-fit, minmax(118px, 1fr)); }
.ar-buy-actions .ar-btn { min-width: 0; white-space: nowrap;
  display: flex; align-items: center; justify-content: center; gap: 6px; }
.ar-buy-actions .ar-btn .ico { display: inline-flex; flex: 0 0 auto; }
.ar-buy-actions .ar-btn .ico svg { display: block; width: 17px; height: 17px; }
/* Collapse the two actions to icon-only when either there isn't width for the
   labelled buttons (narrow) OR the viewport is short, where the labels would eat
   too much vertical space — the same rule the play bar's controls use. */
@media (max-width: 440px) and (max-height: 1000px), (max-height: 600px) {
  .ar-buy-actions { grid-template-columns: repeat(2, 1fr); }
  .ar-buy-actions .ar-btn { padding-left: 4px; padding-right: 4px; }
  .ar-buy-actions .ar-btn .lbl { display: none; }
  .ar-buy-actions .ar-btn .ico svg { width: 22px; height: 22px; }
}

/* Very narrow: stack the budget/buttons column beneath the chips. The strip is now
   its own stacked row, so the grid returns to flow as a single horizontally-
   scrolling row (the absolute fill is only for the side-by-side bar above). */
@media (max-width: 560px) {
  .ar-buy-bar { flex-direction: column; }
  .ar-buy-right { flex: 1 1 auto; max-width: none; justify-content: flex-start; }
  .ar-buy-chips { flex: 0 0 auto; overflow: visible;
    border-right: none; border-bottom: 2px solid var(--wood); box-shadow: inset 0 -1px 0 hsla(0, 0%, 100%, 0.35); }
  .ar-buy-chips-grid { position: static; display: flex; flex-wrap: nowrap; align-items: stretch;
    grid-template-columns: none; overflow: auto hidden; }
  .ar-buy-chips-grid .ar-chip { flex: 0 0 156px; }
}
/* Once the bar has folded (≤560px) on a not-tall screen, drop the empty message
   row entirely (not just its text) to reclaim vertical space — a real per-ship
   message still appears and shows the row again. */
@media (max-width: 560px) and (max-height: 1000px) {
  .ar-buy-msg:empty { display: none; }
}
`;

export interface BuyHandle {
  add(type: ShipType): void;
  remove(type: ShipType): void;
  fillSuggested(): void;
  budgetLeft(): number;
  counts(): Record<ShipType, number>;
  /** number of purchased units (for the E2E / shots) */
  size(): number;
  deploy(): void;
}

interface BuyUnit {
  type: ShipType;
  home: City;
  dest: City | null;
}

export interface BuyScreen {
  handle: BuyHandle;
  done: Promise<void>;
}

export function startBuyScreen(
  boardEl: HTMLElement,
  barHost: HTMLElement,
  view: BoardView,
  g: GameState,
  side: Side,
): BuyScreen {
  if (!document.getElementById('ar-buy-style')) {
    const style = document.createElement('style');
    style.id = 'ar-buy-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const homes = g.cities.filter((c) => c.home === side); // valid spawn ports
  const units: BuyUnit[] = [];
  let selUnit: BuyUnit | null = null; // ship whose ports a map tap sets (touch flow)
  let resolveDone: () => void = () => {};
  const done = new Promise<void>((res) => {
    resolveDone = res;
  });

  // The buy bar is the game's bottom bar: ship chips (left) and the budget/ship
  // readout above the action buttons (right). The enemy-difficulty picker lives in
  // the top bar now (game.ts), and course-setting happens in a separate floating
  // "Fleet orders" window on the left of the board (built just below).
  const bar = document.createElement('div');
  bar.className = 'ar-buy-bar';
  bar.innerHTML = `
    <div class="ar-buy-chips"><div class="ar-buy-chips-grid" id="ar-chips"></div></div>
    <div class="ar-buy-side">
      <div class="ar-buy-right">
        <div class="ar-buy-budget"><span class="ships">Ships: <b id="ar-fleetn">0</b></span>
          <span class="bud">${ICON_VP}<b id="ar-bud">${g.vp[side]}</b></span></div>
        <div class="ar-buy-msg" id="ar-buy-msg"></div>
        <div class="ar-buy-actions">
          <button class="ar-btn" id="ar-buy-suggest" aria-label="Suggest fleet"><span class="ico">${ICON_ZAP}</span><span class="lbl">Suggest fleet</span></button>
          <button class="ar-btn primary" id="ar-buy-go" aria-label="Deploy fleet"><span class="lbl">Deploy fleet</span><span class="ico">${ICON_PLAY}</span></button>
        </div>
      </div>
    </div>`;
  barHost.appendChild(bar);

  // The floating "Fleet orders" window on the left of the board — the roster of
  // every ship bought, each with its course. Same window chrome (.ar-owin, styled
  // in game.ts) and settings-style header as the in-play Orders panel. Click a ship
  // here, then click a port on the board to set its course.
  const win = document.createElement('div');
  win.className = 'ar-owin';
  win.id = 'ar-fleet-win';
  win.style.display = 'none'; // closed by default, opened via the Fleet tab (like in-game)
  boardEl.appendChild(win);
  // the "Fleet" tab shown while the window is closed, to open it
  const reopen = document.createElement('button');
  reopen.className = 'ar-owin-reopen';
  reopen.id = 'ar-fleet-reopen';
  reopen.innerHTML = `${ICON_FLAG}<span>Fleet</span>`;
  boardEl.appendChild(reopen);

  const $ = (id: string): HTMLElement => bar.querySelector(`#${id}`)!;

  // chips — name top-left + price tab top-right, acronym/role description in the
  // middle, then the −/count/+ stepper along the bottom.
  $('ar-chips').innerHTML = ORDER.map(
    (t) => `<div class="ar-chip" data-type="${t}">
      <div class="hd"><span class="nm">${UNITS[t].name}</span><span class="pr">${ICON_VP}${UNITS[t].cost}</span></div>
      <div class="desc">${t} · ${NOTE[t]}</div>
      <div class="ctl"><button data-act="dec" data-type="${t}" aria-label="Remove one ${t}">${ICON_MINUS}</button>
        <span class="n" id="ar-n-${t}">0</span>
        <button data-act="inc" data-type="${t}" aria-label="Add one ${t}">${ICON_PLUS}</button></div>
    </div>`,
  ).join('');

  function spent(): number {
    return units.reduce((a, u) => a + UNITS[u.type].cost, 0);
  }
  function budgetLeft(): number {
    return g.vp[side] - spent();
  }
  function counts(): Record<ShipType, number> {
    const out = Object.fromEntries(ORDER.map((t) => [t, 0])) as Record<ShipType, number>;
    for (const u of units) out[u.type]++;
    return out;
  }

  // Every ship's course is drawn, but the UNSELECTED ones dimmed (via routes) and
  // the selected one prominent (via routeSel) — so the board shows all plans without
  // looking cluttered. (A selected from-only ship shows a lone origin dot via marks.)
  function routes(): [number, number][][] {
    return units
      .filter((u) => u.dest && u !== selUnit)
      .map((u) => [[u.home.c, u.home.r], [u.dest!.c, u.dest!.r]] as [number, number][]);
  }
  function routeSel(): [number, number][] | undefined {
    return selUnit && selUnit.dest
      ? [[selUnit.home.c, selUnit.home.r], [selUnit.dest.c, selUnit.dest.r]]
      : undefined;
  }
  // While the Fleet window is open and the selected ship has a home but no
  // destination yet, show a lone dot on its origin — so a touch on the "from" city
  // is visible before the "to" city completes the arrow.
  function marks(): [number, number][] {
    return winOpen && selUnit && !selUnit.dest ? [[selUnit.home.c, selUnit.home.r]] : [];
  }

  // Show/hide the Fleet window; when hidden, a small "Fleet" tab takes its place to
  // bring it back. Closed by default (matching the in-game orders window).
  let winOpen = false;
  function showFleetWin(open: boolean): void {
    winOpen = open;
    win.style.display = open ? '' : 'none';
    reopen.style.display = open ? 'none' : '';
    if (open) renderFleet();
    else selUnit = null; // closing deselects — no ship stays highlighted on the map
    // fog off for the whole deploy phase — the entire map must be visible to plan
    view.update({ routes: routes(), routeSel: routeSel(), marks: marks(), fog: false }); // clear/redraw the origin dot
  }

  /** Render the fleet-orders window: a settings-style header (close button +
   *  heading) over one row per purchased ship. Each row is compact — name +
   *  course summary + a "+" (set/edit course) and "×" (clear course) button,
   *  mirroring the in-play orders list. Tapping "+" expands that row's From/To
   *  port pickers and makes the ship the target of a map tap. Rebuilt on every
   *  refresh() so it always matches state. */
  function renderFleet(): void {
    const prevBody = win.querySelector<HTMLElement>('.body');
    const scroll = prevBody ? prevBody.scrollTop : 0;
    const rows = units.length
      ? units
          .map((u, i) => {
            const homeOpts = homes
              .map((c, ci) => `<option value="${ci}"${c === u.home ? ' selected' : ''}>${c.name}</option>`)
              .join('');
            // Destinations exclude your own home ports — a ship spawns there, so
            // there is nothing to sail to. (This also makes From ≠ To impossible,
            // since From only lists home ports.) Keep the g.cities index as value.
            const destOpts = `<option value="-1"${u.dest ? '' : ' selected'}>— none —</option>`
              + g.cities
                  .map((c, ci) => (c.home === side
                    ? ''
                    : `<option value="${ci}"${u.dest === c ? ' selected' : ''}>${c.name}</option>`))
                  .join('');
            // Every ship already has a home port from the moment it's added, so the
            // pickers are always meaningful — no +/× toggle. Name + From → To sit
            // inline on one row; a from-only ship (To = "— none —") is a valid
            // "spawn here, no course" choice.
            const on = u === selUnit ? ' on' : '';
            return `<div class="tf${on}" data-i="${i}">`
              + `<span class="nm">${UNITS[u.type].name}</span>`
              + `<select class="from" data-i="${i}">${homeOpts}</select>`
              + `<span class="arr">${ICON_ARROW}</span>`
              + `<select class="to" data-i="${i}">${destOpts}</select>`
              + `</div>`;
          })
          .join('')
      : '<div class="none">No ships yet — buy some below, Admiral.</div>';
    win.innerHTML = `<div class="hd"><button class="cls" id="ar-fleet-x" title="Close">${ICON_X}<span>Close</span></button>`
      + `<h3>${ICON_FLAG}Fleet</h3></div>`
      + `<div class="body"><p class="hint">Set each ship's home &amp; optional destination — menus, or tap the map.</p>${rows}</div>`;
    win.querySelector('#ar-fleet-x')?.addEventListener('click', () => showFleetWin(false));
    // tap a row (not its menus) to make it the active ship for map-tapping
    win.querySelectorAll<HTMLElement>('.tf').forEach((row) => {
      row.addEventListener('click', (ev) => {
        if ((ev.target as HTMLElement).closest('select')) return; // let the dropdown open
        const u = units[Number(row.dataset['i'])];
        if (!u) return;
        selUnit = u;
        // The Fleet window row already shows this ship's course, so leave the
        // bottom-bar instruction line as-is (don't echo the selection here).
        refresh();
      });
    });
    win.querySelectorAll<HTMLSelectElement>('select.from').forEach((selEl) => {
      selEl.addEventListener('change', () => {
        const u = units[Number(selEl.dataset['i'])];
        const h = homes[Number(selEl.value)];
        if (u && h) { u.home = h; selUnit = u; refresh(); }
      });
    });
    win.querySelectorAll<HTMLSelectElement>('select.to').forEach((selEl) => {
      selEl.addEventListener('change', () => {
        const u = units[Number(selEl.dataset['i'])];
        if (!u) return;
        const v = Number(selEl.value);
        u.dest = v < 0 ? null : (g.cities[v] ?? null);
        selUnit = u;
        refresh();
      });
    });
    const body = win.querySelector<HTMLElement>('.body');
    if (body) body.scrollTop = scroll;
  }
  reopen.addEventListener('click', () => showFleetWin(true));

  function refresh(): void {
    const c = counts();
    for (const t of ORDER) $(`ar-n-${t}`).textContent = String(c[t]);
    // enable/disable each stepper by what the player can actually do: + only when the
    // unit is affordable (and a home port exists), − only when one is owned.
    const left = budgetLeft();
    bar.querySelectorAll<HTMLButtonElement>('button[data-act]').forEach((b) => {
      const t = b.dataset['type'] as ShipType;
      b.disabled = b.dataset['act'] === 'inc'
        ? left < UNITS[t].cost || homes.length === 0
        : c[t] === 0;
    });
    $('ar-bud').textContent = `${budgetLeft()}`;
    $('ar-fleetn').textContent = String(units.length);
    (($('ar-buy-go')) as HTMLButtonElement).disabled = units.length === 0;
    renderFleet();
    view.update({ routes: routes(), routeSel: routeSel(), marks: marks(), fog: false });
  }

  function add(type: ShipType): void {
    if (budgetLeft() < UNITS[type].cost || !homes.length) return;
    const home = homes[units.length % homes.length]!; // sensible default spawn; editable in the Fleet window
    const unit: BuyUnit = { type, home, dest: null };
    units.push(unit);
    selUnit = unit; // the newest ship becomes selected (highlighted when the Fleet window is open)
    refresh();
  }

  function remove(type: ShipType): void {
    for (let i = units.length - 1; i >= 0; i--) {
      if (units[i]!.type === type) {
        if (units[i] === selUnit) selUnit = null;
        units.splice(i, 1);
        break;
      }
    }
    refresh();
  }

  function fillSuggested(): void {
    units.length = 0;
    const { buys } = suggestFleet(g.vp[side]);
    buys.forEach((t, i) => units.push({ type: t, home: homes[i % homes.length]!, dest: null }));
    selUnit = null;
    refresh();
  }

  function deploy(): void {
    if (!units.length) return;
    units.forEach((u) => {
      const ship = addShip(g, side, u.type, u.home.c, u.home.r);
      // carry the planned course onto the ship as its standing order; the movement
      // screen draws it as a dotted trail and lets you edit it. Planning aid only —
      // the rules never move a ship on its own.
      if (u.dest) ship.dest = [u.dest.c, u.dest.r];
    });
    separateShips(g); // one ship per tile — spread the fleet off its home ports
    g.vp[side] = budgetLeft(); // whatever's left carries as opening score
    cleanup();
    resolveDone();
  }

  function cleanup(): void {
    view.renderer.domElement.removeEventListener('click', onBoardClick);
    // drop the icon-only breakpoint listener too — otherwise, after Deploy, a
    // viewport crossing the breakpoint fires syncActionTips against the removed bar.
    buyIconOnly.removeEventListener('change', syncActionTips);
    bar.remove();
    win.remove();
    reopen.remove();
    view.update({ routes: [] });
  }

  // Touch/click a port on the map to set the selected ship's ports: one of your
  // own (home) cities sets the ORIGIN; any other city sets the DESTINATION. The
  // From/To menus do the same precisely — this is the touch-friendly alternative.
  function onBoardClick(ev: MouseEvent): void {
    if (!selUnit || !winOpen) return; // only set courses while the Fleet window is open
    const hex = view.screenToHex(ev.clientX, ev.clientY);
    if (!hex) return;
    const city = g.cities.find((c) => c.c === hex[0] && c.r === hex[1]);
    if (!city) return;
    if (city.home === side) {
      selUnit.home = city; // your own city → origin/spawn
    } else {
      selUnit.dest = (selUnit.dest === city) ? null : city; // tap again to clear
    }
    refresh();
  }

  // +/- steppers: a plain press = one step; holding past HOLD_DELAY repeats every 90ms
  // (mirrors the zoom buttons), self-stopping when the action is no longer valid (out
  // of budget for +, or count hits 0 for −).
  const HOLD_DELAY = 350;
  bar.querySelectorAll<HTMLButtonElement>('button[data-act]').forEach((b) => {
    const t = b.dataset['type'] as ShipType;
    const inc = b.dataset['act'] === 'inc';
    const step = (): void => { if (inc) add(t); else remove(t); };
    const canStep = (): boolean => inc ? budgetLeft() >= UNITS[t].cost && homes.length > 0 : counts()[t] > 0;
    let delayTimer = 0;
    let repeatTimer = 0;
    const stop = (): void => {
      if (delayTimer) { clearTimeout(delayTimer); delayTimer = 0; }
      if (repeatTimer) { clearInterval(repeatTimer); repeatTimer = 0; }
    };
    b.addEventListener('pointerdown', (ev) => {
      if (b.disabled) return;
      ev.preventDefault();
      step(); // single step on press
      stop();
      delayTimer = window.setTimeout(() => {
        repeatTimer = window.setInterval(() => { if (canStep()) step(); else stop(); }, 90);
      }, HOLD_DELAY);
    });
    for (const e of ['pointerup', 'pointerleave', 'pointercancel'] as const) b.addEventListener(e, stop);
  });
  $('ar-buy-suggest').addEventListener('click', fillSuggested);
  $('ar-buy-go').addEventListener('click', deploy);
  // Hover tooltips describing each action — only while the labels are hidden
  // (icon-only layout); when the words show, a tooltip would be redundant. Mirrors
  // the play screen's icon-only tooltip handling (same breakpoint).
  const buyIconOnly = window.matchMedia('(max-width: 440px) and (max-height: 1000px), (max-height: 600px)');
  const syncActionTips = (): void => {
    const tip = buyIconOnly.matches;
    for (const [id, text] of [['ar-buy-suggest', 'Suggest fleet'], ['ar-buy-go', 'Deploy fleet']] as const) {
      if (tip) $(id).title = text; else $(id).removeAttribute('title');
    }
  };
  buyIconOnly.addEventListener('change', syncActionTips);
  syncActionTips();
  view.renderer.domElement.addEventListener('click', onBoardClick);

  refresh();

  const handle: BuyHandle = {
    add, remove, fillSuggested, budgetLeft, counts,
    size: () => units.length,
    deploy,
  };
  return { handle, done };
}
